import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Menu, X, ChevronRight, Phone, Mail, MapPin, Check, ArrowRight, Users, Award, TrendingUp, Building2, Heart, Briefcase, Sparkles, BarChart3, BookOpen, Linkedin } from "lucide-react";
import Chatbot from "./components/Chatbot";
import LandingPage from "./components/LandingPage";
import SiteContext, { useSiteContext } from "./SiteContext";
import DesignPreview from "./components/DesignPreview";
import PreviewSite from "./components/PreviewSite";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfUse from "./components/TermsOfUse";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Navigation Component - Apple-inspired minimal design
const Navigation = ({ basePath = '' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => location.pathname === `${basePath}${path}`;

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'nav-minimal' : 'bg-white/80 backdrop-blur-md'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img src="/spire-logo.png" alt="Spire Group" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-2">
            <Link to={`${basePath}/`} className={`nav-link-minimal ${isActive('/') ? 'text-spire-purple' : ''}`}>Home</Link>
            <Link to={`${basePath}/our-story`} className={`nav-link-minimal ${isActive('/our-story') ? 'text-spire-purple' : ''}`}>Our Story</Link>
            <Link to={`${basePath}/services`} className={`nav-link-minimal ${isActive('/services') ? 'text-spire-purple' : ''}`}>Services</Link>
            <Link to={`${basePath}/our-method`} className={`nav-link-minimal ${isActive('/our-method') ? 'text-spire-purple' : ''}`}>Our Method</Link>
            <Link to={`${basePath}/team`} className={`nav-link-minimal ${isActive('/team') ? 'text-spire-purple' : ''}`}>Team</Link>
            <Link to={`${basePath}/contact`} className="ml-4 btn-spire-primary">Schedule Consultation</Link>
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-gray-900" onClick={() => setIsMenuOpen(!isMenuOpen)}>
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 animate-fade-in">
            <div className="flex flex-col space-y-2">
              <Link to={`${basePath}/`} className="nav-link-minimal block" onClick={() => setIsMenuOpen(false)}>Home</Link>
              <Link to={`${basePath}/our-story`} className="nav-link-minimal block" onClick={() => setIsMenuOpen(false)}>Our Story</Link>
              <Link to={`${basePath}/services`} className="nav-link-minimal block" onClick={() => setIsMenuOpen(false)}>Services</Link>
              <Link to={`${basePath}/our-method`} className="nav-link-minimal block" onClick={() => setIsMenuOpen(false)}>Our Method</Link>
              <Link to={`${basePath}/team`} className="nav-link-minimal block" onClick={() => setIsMenuOpen(false)}>Team</Link>
              <Link to={`${basePath}/contact`} className="btn-spire-primary mt-4 text-center" onClick={() => setIsMenuOpen(false)}>Schedule Consultation</Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Footer Component  
const Footer = () => {
  const { healthcareOnly } = useSiteContext();
  
  return (
    <footer className="footer-minimal">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-12">
          {/* Company Info */}
          <div className="col-span-1 md:col-span-5">
            <img src="/spire-logo.png" alt="Spire Group" className="h-10 w-auto mb-6" />
            <p className="text-gray-600 text-base leading-relaxed mb-6 max-w-md">
              {healthcareOnly 
                ? "Leading healthcare real estate consulting firm in North America, helping healthcare professionals make informed strategic decisions with decades of proven expertise."
                : "Leading healthcare and commercial real estate consulting firm in North America, helping businesses make informed strategic decisions with decades of proven expertise."
              }
            </p>
            <div className="flex space-x-4">
              <a href="https://www.linkedin.com/company/the-spire-group" target="_blank" rel="noopener noreferrer" 
                 className="w-10 h-10 rounded-full bg-gray-100 hover:bg-spire-purple hover:text-white flex items-center justify-center transition-all">
                <Linkedin size={20} />
              </a>
            </div>
          </div>

          {/* Services */}
          <div className="col-span-1 md:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Services</h3>
            <ul className="space-y-3">
              <li><Link to="/services" className="text-gray-600 hover:text-spire-purple transition text-sm">{healthcareOnly ? 'Healthcare' : 'Healthcare'} Real Estate</Link></li>
              {!healthcareOnly && (
                <li><Link to="/services" className="text-gray-600 hover:text-spire-purple transition text-sm">Commercial Real Estate</Link></li>
              )}              <li><Link to="/our-method" className="text-gray-600 hover:text-spire-purple transition text-sm">Our Method</Link></li>
            </ul>
          </div>

          <div className="col-span-1 md:col-span-2">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Company</h3>
            <ul className="space-y-3">
              <li><Link to="/our-story" className="text-gray-600 hover:text-spire-purple transition text-sm">Our Story</Link></li>
              <li><Link to="/team" className="text-gray-600 hover:text-spire-purple transition text-sm">Meet the Team</Link></li>
              <li><Link to="/contact" className="text-gray-600 hover:text-spire-purple transition text-sm">Contact</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div className="col-span-1 md:col-span-3">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">Office</h3>
            <ul className="space-y-3">
              <li className="flex items-center text-sm">
                <Phone size={16} className="mr-2 text-spire-purple flex-shrink-0" />
                
              </li>
              <li className="flex items-center text-sm">
                <Mail size={16} className="mr-2 text-spire-purple flex-shrink-0" />
                <a href="mailto:info@spiregrp.com" className="text-gray-600 hover:text-spire-purple transition">info@spiregrp.com</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} Spire Group Inc. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy" className="text-sm text-gray-500 hover:text-spire-purple transition">Privacy Policy</Link>
            <Link to="/terms" className="text-sm text-gray-500 hover:text-spire-purple transition">Terms of Use</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Smileville Dental Testimonial with expandable modal
const SmilevilleTestimonial = () => {
  const [isOpen, setIsOpen] = useState(false);

  const fullText = [
    "Jeff has been our real estate consultant since 2017, and quite honestly, we would not be where we are today without his guidance.",
    "When we first began exploring spaces for our dental clinics, we had big goals but also a lot of questions. Jeff was there every step of the way — not just sending listings, but truly understanding our vision, our brand, and what would set us up for long term success. He helped us search strategically, negotiate thoughtfully, and ultimately acquire two leased spaces that have become the foundation of our growing practice.",
    "What sets Jeff apart is that he does not see real estate as just a transaction. He genuinely cares about the people behind the business. His passion for helping healthcare professionals succeed is evident in everything he does — from connecting us with the right brokers, landlords, and industry contacts, to sharing insights that only come from years of experience in the healthcare real estate space.",
    "He is knowledgeable, calm under pressure, incredibly responsive, and always acts with integrity. There were moments during negotiations and due diligence where his steady guidance made all the difference. He consistently looks beyond the immediate deal and focuses on what will serve you best five or ten years down the road.",
    "We are deeply grateful for Jeff's support over the years and consider him a trusted advisor, not just a consultant. We would recommend him wholeheartedly to any healthcare professional looking to secure the right space and build a strong foundation for their practice."
  ];

  return (
    <>
      <div className="card-elevated flex flex-col">
        <div className="text-5xl text-spire-green mb-4">"</div>
        <p className="text-lg text-gray-700 leading-relaxed mb-4 flex-1">
          Jeff has been our real estate consultant since 2017, and quite honestly, we would not be where we are today without his guidance. He helped us search strategically, negotiate thoughtfully, and ultimately acquire two leased spaces that have become the foundation of our growing practice.
        </p>
        <div className="flex items-center justify-between mt-2">
          <div>
            <p className="font-semibold text-spire-green">— Dr. Arohi Talwar & Shefali Marwaha</p>
            <p className="text-sm text-gray-500">Smileville Dental</p>
          </div>
          <button
            onClick={() => setIsOpen(true)}
            className="flex items-center gap-1 text-sm font-semibold text-spire-green hover:text-spire-green-dark transition-colors ml-4 flex-shrink-0 underline underline-offset-2"
          >
            Read full story
          </button>
        </div>
      </div>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setIsOpen(false)}>
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-10 relative"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-5 right-5 w-9 h-9 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <X size={18} />
            </button>

            <div className="text-6xl text-spire-green mb-4 leading-none">"</div>
            <div className="space-y-4 mb-8">
              {fullText.map((para, i) => (
                <p key={i} className="text-gray-700 leading-relaxed">{para}</p>
              ))}
            </div>
            <div className="border-t border-gray-100 pt-6">
              <p className="font-bold text-lg text-spire-green">— Dr. Arohi Talwar & Shefali Marwaha</p>
              <p className="text-gray-500 font-medium">Smileville Dental</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// Home Page
const Home = () => {
  const { healthcareOnly } = useSiteContext();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Background Image */}
      <section className="relative min-h-[90vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/7e7eafde-3b50-4d50-9eac-c263265d5841/images/506d652e13a8f79ddb4f072c73c720b944d49bd06acd7b8e223155022823a37f.png" 
            alt="Healthcare Real Estate" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/55 to-black/30"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight drop-shadow-2xl">
              Healthcare<br />
              Real Estate<br />
              Excellence.<br />
              <span className="text-spire-green">Simplified.</span>
            </h1>
            <p className="text-xl md:text-2xl text-white mb-10 leading-relaxed font-medium drop-shadow-lg bg-black/30 backdrop-blur-sm p-6 rounded-2xl border border-white/10">
              {healthcareOnly 
                ? "Your trusted partner for healthcare real estate in North America. With a century of combined expertise, we help healthcare professionals secure the perfect space for their practice."
                : "Your trusted partner for healthcare and commercial real estate in North America. With a century of combined expertise, we turn complex real estate decisions into successful outcomes."
              }
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-spire-primary text-lg group">
                Schedule Consultation 
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            </div>

            {/* Stats */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="backdrop-blur-sm bg-white/10 p-4 rounded-xl border border-white/20 flex md:block items-center gap-4">
                <div className="text-3xl md:text-5xl font-bold text-spire-green drop-shadow-lg shrink-0">4,000+</div>
                <div>
                  <div className="text-white font-semibold text-sm leading-tight">Commercial Agreements</div>
                  <div className="text-white/70 text-xs leading-tight">for healthcare professionals across North America</div>
                </div>
              </div>
              <div className="backdrop-blur-sm bg-white/10 p-4 rounded-xl border border-white/20 flex md:block items-center gap-4">
                <div className="text-3xl md:text-5xl font-bold text-spire-green drop-shadow-lg shrink-0">$600M</div>
                <div>
                  <div className="text-white font-semibold text-sm leading-tight">In Avoidable Costs</div>
                  <div className="text-white/70 text-xs leading-tight">saved for our clients through expert negotiation</div>
                </div>
              </div>
              <div className="backdrop-blur-sm bg-white/10 p-4 rounded-xl border border-white/20 flex md:block items-center gap-4">
                <div className="text-3xl md:text-5xl font-bold text-spire-green drop-shadow-lg shrink-0">100+</div>
                <div>
                  <div className="text-white font-semibold text-sm leading-tight">Years Combined</div>
                  <div className="text-white/70 text-xs leading-tight">experience in healthcare real estate consulting</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What's Important Section */}
      <section className="section-padding bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">We pride ourselves on our ability to listen</h2>
            <p className="text-2xl md:text-3xl text-spire-purple font-semibold mb-8">What's important to you?</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="text-5xl mb-4">🎯</div>
              <h3 className="text-xl font-bold mb-3">Your Vision</h3>
              <p className="text-gray-600">We understand your requirements and expectations are unique to you and your business.</p>
            </div>
            <div className="text-center p-6">
              <div className="text-5xl mb-4">💼</div>
              <h3 className="text-xl font-bold mb-3">Your Needs</h3>
              <p className="text-gray-600">We listen for what you need to make your wants a reality, finding the balance between wants and needs.</p>
            </div>
            <div className="text-center p-6">
              <div className="text-5xl mb-4">🤝</div>
              <h3 className="text-xl font-bold mb-3">Your Success</h3>
              <p className="text-gray-600">Our commitment: Listen to the client and make sure that every deal is a WIN:WIN.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="section-title-large mb-4">Our Specialized Services</h2>
            <p className="section-subtitle-large">Expert consulting tailored to your industry</p>
          </div>

          <div className={`grid ${healthcareOnly ? 'md:grid-cols-1 max-w-3xl mx-auto' : 'md:grid-cols-2'} gap-8`}>
            {/* Healthcare Card */}
            <Link to="/services" className="group relative overflow-hidden rounded-3xl">
              <div className="absolute inset-0">
                <img 
                  src="https://images.unsplash.com/photo-1746021375306-9dec0f637732?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" 
                  alt="Healthcare" 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-spire-purple via-spire-purple/80 to-transparent"></div>
              </div>
              <div className="relative p-10 md:p-12 min-h-[500px] flex flex-col justify-end">
                <div className="icon-circle mb-6">
                  <Heart className="text-white" size={32} />
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                  {healthcareOnly ? 'Healthcare' : 'Healthcare'}<br />Real Estate
                </h3>
                <p className="text-lg text-white/90 mb-6">
                  Comprehensive consulting for healthcare professionals. From practice site selection to lease negotiations.
                </p>
                <div className="flex items-center text-white font-semibold">
                  Learn More <ChevronRight className="ml-2 group-hover:translate-x-2 transition-transform" size={20} />
                </div>
              </div>
            </Link>

            {/* Commercial Card - Hide in healthcare-only mode */}
            {!healthcareOnly && (
              <Link to="/full-site/services" className="group relative overflow-hidden rounded-3xl">
                <div className="absolute inset-0">
                  <img 
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=85&w=800" 
                    alt="Commercial" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-spire-green via-spire-green-dark/80 to-transparent"></div>
                </div>
                <div className="relative p-10 md:p-12 min-h-[500px] flex flex-col justify-end">
                  <div className="icon-circle-green mb-6">
                    <Building2 className="text-white" size={32} />
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold mb-4 text-white">Commercial<br />Real Estate</h3>
                  <p className="text-lg text-white/90 mb-6">
                    Strategic solutions for retail, office, and industrial businesses. Maximize value and minimize risk.
                  </p>
                  <div className="flex items-center text-white font-semibold">
                    Learn More <ChevronRight className="ml-2 group-hover:translate-x-2 transition-transform" size={20} />
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="section-title-large mb-4">Why Spire Group?</h2>
            <p className="section-subtitle-large">Unmatched expertise and dedication to your success</p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-spire-purple to-spire-purple-light flex items-center justify-center mx-auto mb-6">
                <Users className="text-white" size={36} />
              </div>
              <h3 className="text-2xl font-bold mb-4">Expert Team</h3>
              <p className="text-gray-600 leading-relaxed">Seasoned professionals with decades of combined experience in healthcare and commercial real estate across North America.</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-spire-green to-spire-green-light flex items-center justify-center mx-auto mb-6">
                <Award className="text-white" size={36} />
              </div>
              <h3 className="text-2xl font-bold mb-4">Proven Results</h3>
              <p className="text-gray-600 leading-relaxed">Track record of successful transactions and satisfied clients across diverse real estate sectors throughout Calgary and Alberta.</p>
            </div>
            <div className="text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-spire-purple to-spire-green flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="text-white" size={36} />
              </div>
              <h3 className="text-2xl font-bold mb-4">Strategic Insights</h3>
              <p className="text-gray-600 leading-relaxed">Data-driven market analysis and strategic advice to maximize value and minimize risk in every transaction.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Home Testimonials */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16">Trusted by Leading Professionals</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-elevated">
              <div className="text-5xl text-spire-purple mb-4">"</div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Thank you for doing a thorough analysis of my lease. You most importantly considered my current and future circumstances and made recommendations that were ideal for me and my practice. I would gladly recommend your services to anyone.
              </p>
              <p className="font-semibold text-spire-purple">— Anna Rankin, BSc, DMD</p>
            </div>

            <SmilevilleTestimonial />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" style={{background: 'linear-gradient(135deg, #652D89 0%, #8B4DB3 100%)'}}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to make informed<br />real estate decisions?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Schedule a consultation with our expert team today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="px-10 py-4 bg-white text-spire-purple font-semibold rounded-full hover:shadow-2xl transition-all inline-flex items-center justify-center text-lg">
              Get Started <ArrowRight className="ml-2" size={20} />
            </Link>
            <Link to="/our-story" className="px-10 py-4 bg-spire-green text-white font-semibold rounded-full hover:bg-spire-green-dark transition-all inline-flex items-center justify-center text-lg">
              Learn About Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

// Team Page Component with real bios
const Team = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const teamMembers = [
    {
      name: "Michael Permack",
      photo: "/team-michael.jpg",
      email: "m.permack@spiregrp.com",
      phone: "403.462.5560",
      linkedin: "http://ca.linkedin.com/in/michaelpermack",
      calendly: "https://calendly.com/michaelpermack/"
    },
    {
      name: "Jeff Sklar",
      photo: "/team-jeff.jpg",
      email: "j.sklar@spiregrp.com",
      phone: "403.483.5561",
      linkedin: "http://ca.linkedin.com/pub/jeff-sklar/4a/533/750"
    },
    {
      name: "Jason Shantz",
      photo: "/team-jason.jpg",
      email: "j.shantz@spiregrp.com",
      phone: "403.874.1129",
      linkedin: "https://www.linkedin.com/in/jason-shantz-5561a131"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1758448721149-aa0ce8e1b2c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
            alt="Premium Healthcare Lobby"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-spire-purple/90 to-purple-900/75"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Meet Our Team</h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Experienced professionals dedicated to your real estate success in North America.
          </p>
        </div>
      </section>

      {/* Team Members */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-12">
            {teamMembers.map((member, index) => (
              <div key={index} className="card-elevated text-center group">
                <div className="mb-6 overflow-hidden rounded-2xl">
                  <img 
                    src={member.photo} 
                    alt={member.name}
                    className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-2xl font-bold mb-6">{member.name}</h3>
                
                <div className="space-y-3 text-sm text-gray-600 mb-6">
                  <p className="flex items-center justify-center">
                    <Mail size={16} className="mr-2 text-spire-purple" />
                    <a href={`mailto:${member.email}`} className="hover:text-spire-purple transition">{member.email}</a>
                  </p>
                  <p className="flex items-center justify-center">
                    <Phone size={16} className="mr-2 text-spire-purple" />
                    <a href={`tel:${member.phone.replace(/\./g, '-')}`} className="hover:text-spire-purple transition">{member.phone}</a>
                  </p>
                </div>

                <div className="space-y-3">
                  {member.calendly && (
                    <a 
                      href={member.calendly} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block w-full btn-spire-primary mb-3"
                    >
                      Schedule a Meeting
                    </a>
                  )}
                  
                  <a 
                    href={member.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-spire-purple hover:text-spire-purple-light transition font-semibold"
                  >
                    <Linkedin size={20} className="mr-2" />
                    Connect on LinkedIn
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Client Testimonials */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16">What Our Clients Say</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="card-elevated">
              <div className="text-5xl text-spire-purple mb-4">"</div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                I was very impressed with Spire Group. Jeff Sklar represented me and I would highly recommend utilizing his services for both location selection and lease negotiation. His local knowledge of the Calgary marketplace is invaluable. His thorough and detailed approach to lease negotiation ensured my interests were protected in the short term and in the future.
              </p>
              <p className="font-semibold text-spire-purple">— Dr. Jana Boychuk, DDS</p>
            </div>

            <div className="card-elevated">
              <div className="text-5xl text-spire-green mb-4">"</div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We can't thank Spire enough for their help with our recent expansion. Their attention to detail and expertise in the real estate market exceeded our expectations. The Spire team provided us with peace of mind throughout the entire process and they definitely had our best interests in mind.
              </p>
              <p className="font-semibold text-spire-green">— Dr. C. Oriel, DDS</p>
            </div>

            <div className="card-elevated">
              <div className="text-5xl text-spire-purple mb-4">"</div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Spire's anticipation of our needs, knowledge of commercial leasing and their willingness to call a spade a spade are one of the rarest combinations in almost any field, let alone commercial real estate. The Spire team treated us as if we were their only client and they were always available to answer our questions.
              </p>
              <p className="font-semibold text-spire-purple">— Dr. S. Gupta, DDS</p>
            </div>

            <div className="card-elevated">
              <div className="text-5xl text-spire-green mb-4">"</div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                I have found Spire to be responsive, knowledgeable and well connected. With Spire's assistance, I was able to negotiate the lease I wanted and needed most. I would never have negotiated this lease renewal without them.
              </p>
              <p className="font-semibold text-spire-green">— Kelly Barrie, Owner/Physiotherapist, Fifth Avenue Physiotherapy</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Work With Our Team?</h2>
          <p className="text-xl text-gray-600 mb-10">
            Schedule a consultation today and discover how we can help achieve your real estate goals.
          </p>
          <Link to="/contact" className="btn-spire-primary text-lg">
            Schedule Consultation <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Our Story Page
const OurStory = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20 bg-white">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1769698678497-c41f0ab47c3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
            alt="Modern Medical Centre Building"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-spire-purple/90 to-spire-purple/70"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Our Story</h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Decades of real estate excellence in North America
          </p>
        </div>
      </section>

      {/* Vision Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <img 
                src="https://images.unsplash.com/photo-1497366811353-6870744d04b2?q=85&w=800" 
                alt="Our Vision" 
                className="rounded-3xl shadow-2xl"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-4xl font-bold mb-6">You have a unique vision for your business</h2>
              <div className="space-y-4 text-lg text-gray-600 leading-relaxed">
                <p>
                  At Spire Group, we understand that your requirements and expectations are unique to you and your business. With extensive combined experience in North America's real estate market, we've built our reputation on one fundamental principle: listening.
                </p>
                <p>
                  We pride ourselves on our ability to truly hear what you want in a property for your business use or investment purposes, and we listen for what you need to make your vision a reality. We find the balance between wants and needs.
                </p>
                <p>
                  Most people only do a few real estate transactions in their lives—we do them every day. This expertise allows us to navigate complex markets, anticipate challenges, and create opportunities that others might miss.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Our Approach</h2>
            <div className="card-elevated">
              <div className="space-y-6 text-lg text-gray-700 leading-relaxed">
                <p>
                  We work together with you and your team to achieve your goals. We start by understanding your vision, then develop a comprehensive plan that aligns with your business objectives and budget.
                </p>
                <p>
                  With a plan in hand, we navigate the market and help you make informed decisions. Smart alternatives are generated, and decisions are made that maximize flexibility, reduce risk, and achieve your goals.
                </p>
                <p>
                  We ensure all documents reflect the negotiated business points and more. We also stay by your side after the ink dries to ensure you get the results you desire and deserve.
                </p>
                <p>
                  Our commitment is simple: Listen to the client and make sure that every deal is a WIN:WIN.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-bold mb-12 text-center">Our Core Values</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-spire-purple to-spire-purple-light flex items-center justify-center mx-auto mb-6">
                <Users className="text-white" size={36} />
              </div>
              <h3 className="text-2xl font-bold mb-4">Client-First Focus</h3>
              <p className="text-gray-600">Your success drives everything we do. We listen, understand, and deliver solutions tailored to your unique needs.</p>
            </div>
            <div className="text-center p-8">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-spire-green to-spire-green-light flex items-center justify-center mx-auto mb-6">
                <Award className="text-white" size={36} />
              </div>
              <h3 className="text-2xl font-bold mb-4">Integrity & Trust</h3>
              <p className="text-gray-600">We conduct business with the highest ethical standards, building lasting relationships based on honesty and transparency.</p>
            </div>
            <div className="text-center p-8">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-spire-purple to-spire-green flex items-center justify-center mx-auto mb-6">
                <TrendingUp className="text-white" size={36} />
              </div>
              <h3 className="text-2xl font-bold mb-4">Market Expertise</h3>
              <p className="text-gray-600">Deep knowledge of North America's real estate landscape enables us to identify opportunities and navigate challenges.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-spire-purple">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Work Together?</h2>
          <p className="text-xl text-white/90 mb-10">
            Let's discuss how we can help you achieve your real estate goals.
          </p>
          <Link to="/contact" className="px-10 py-4 bg-white text-spire-purple font-semibold rounded-full hover:shadow-2xl transition-all inline-flex items-center justify-center text-lg">
            Schedule Consultation <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Services Page
const Services = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20 bg-white">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1746021375306-9dec0f637732?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400" 
            alt="Modern Medical Clinic Interior" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-spire-purple/90 to-spire-purple/70"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-32">
          <div className="max-w-3xl text-white">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Services</h1>
            <p className="text-xl md:text-2xl leading-relaxed">
              Specialized real estate consulting for healthcare professionals across North America.
            </p>
          </div>
        </div>
      </section>

      {/* Healthcare Services */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-center mb-12">
            <div className="icon-circle">
              <Heart className="text-white" size={36} />
            </div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-6">Healthcare Real Estate</h2>
          <p className="text-xl text-center text-gray-600 mb-16 max-w-3xl mx-auto">
            We empower healthcare professionals to make smart real estate decisions through expert guidance and strategic planning.
          </p>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div className="card-feature">
              <h3 className="text-2xl font-bold mb-4">Site Selection & Demographics</h3>
              <p className="text-gray-600 mb-4">
                Strategic location analysis ensuring optimal patient access, visibility, and practice growth potential. We produce detailed demographic reports and competitive analyses to identify the most advantageous locations.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start text-gray-700">
                  <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                  Demographic and market analysis
                </li>
                <li className="flex items-start text-gray-700">
                  <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                  Competition assessment
                </li>
                <li className="flex items-start text-gray-700">
                  <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                  Accessibility and parking evaluation
                </li>
                <li className="flex items-start text-gray-700">
                  <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                  Site feasibility studies
                </li>
              </ul>
            </div>

            <div className="card-feature">
              <h3 className="text-2xl font-bold mb-4">Lease Negotiation</h3>
              <p className="text-gray-600 mb-4">
                Expert negotiation securing favorable terms and protecting your interests in healthcare office leases. We analyze current market trends and rental rates to ensure optimal agreements.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start text-gray-700">
                  <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                  Lease term optimization
                </li>
                <li className="flex items-start text-gray-700">
                  <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                  Rent and escalation analysis
                </li>
                <li className="flex items-start text-gray-700">
                  <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                  Tenant improvement negotiations
                </li>
                <li className="flex items-start text-gray-700">
                  <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                  Risk mitigation strategies
                </li>
              </ul>
            </div>

            <div className="card-feature">
              <h3 className="text-2xl font-bold mb-4">Healthcare Facility Planning</h3>
              <p className="text-gray-600 mb-4">
                Comprehensive planning for healthcare facilities, from small practices to large health centres.
              </p>
              <ul className="space-y-2">
                <li className="flex items-start text-gray-700">
                  <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                  Space planning and layout design
                </li>
                <li className="flex items-start text-gray-700">
                  <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                  Construction support
                </li>
                <li className="flex items-start text-gray-700">
                  <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                  Regulatory compliance guidance
                </li>
                <li className="flex items-start text-gray-700">
                  <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                  Build-out coordination
                </li>
              </ul>
            </div>

            <div className="card-feature">
              <h3 className="text-2xl font-bold mb-4">Practice Types Served</h3>
              <p className="text-gray-600 mb-5">
                We work exclusively with healthcare professionals across every discipline, securing the right spaces for patient-facing practices of all sizes.
              </p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                {[
                  'Family medicine clinics',
                  'Dental offices & DSOs',
                  'Pharmacies',
                  'Optometry practices',
                  'Physiotherapy & rehab',
                  'Chiropractic clinics',
                  'Specialist practices',
                  'Mental health & therapy',
                  'Urgent care centres',
                  'Veterinary clinics',
                  'Medical aesthetics',
                  'Allied health practices',
                ].map((type) => (
                  <div key={type} className="flex items-center text-gray-700 text-sm">
                    <Check className="text-spire-green mr-2 flex-shrink-0" size={15} />
                    {type}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-gray-600 mb-10">
            Schedule a consultation to discuss your real estate needs.
          </p>
          <Link to="/contact" className="btn-spire-primary text-lg">
            Schedule Consultation <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16">Client Success Stories</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-elevated">
              <div className="text-4xl text-spire-purple mb-4">"</div>
              <p className="text-gray-700 leading-relaxed mb-4">
                I want to thank you for all your hard work in helping with all the preliminary steps of establishing my own office. Your continued support and communication was appreciated. Spire did outstanding work for us.
              </p>
              <p className="font-semibold text-spire-purple text-sm">— Dr. Daniel Charland, DDS</p>
            </div>

            <SmilevilleTestimonial />

            <div className="card-elevated">
              <div className="text-4xl text-spire-purple mb-4">"</div>
              <p className="text-gray-700 leading-relaxed mb-4">
                We are very appreciative of the support we received from Spire on our lease renewal. They listened to our needs and created value both in terms of saving us money and getting us favorable lease terms.
              </p>
              <p className="font-semibold text-spire-purple text-sm">— Nelson Halpern, CA, Managing Partner, Kahn Halpern Marshall</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Our Method Page
const OurMethod = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20 bg-white">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1746021375258-79fa1464ca1f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
            alt="Modern Healthcare Office"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-spire-green/90 to-spire-purple/80"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Our Method</h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            A proven approach to real estate success, refined over decades
          </p>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-16">Our Four-Step Process</h2>
          
          <div className="space-y-16">
            {/* Step 1 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-spire-purple to-spire-purple-light flex items-center justify-center text-white text-2xl font-bold mr-4">
                    1
                  </div>
                  <h3 className="text-3xl font-bold">Listen & Understand</h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  We start by listening to your vision, goals, and requirements. Understanding your unique needs is the foundation of our approach. We take the time to learn about your business, budget, timeline, and long-term objectives to ensure we're perfectly aligned with your vision.
                </p>
              </div>
              <div className="bg-gradient-to-br from-spire-purple/10 to-transparent rounded-3xl p-8 h-64 flex items-center justify-center">
                <Users className="text-spire-purple" size={80} />
              </div>
            </div>

            {/* Step 2 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1 bg-gradient-to-br from-spire-green/10 to-transparent rounded-3xl p-8 h-64 flex items-center justify-center">
                <BarChart3 className="text-spire-green" size={80} />
              </div>
              <div className="order-1 md:order-2">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-spire-green to-spire-green-light flex items-center justify-center text-white text-2xl font-bold mr-4">
                    2
                  </div>
                  <h3 className="text-3xl font-bold">Research & Analyze</h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  We conduct comprehensive market research, demographic analysis, and competitive assessments. Our deep knowledge of North America's real estate landscape allows us to identify opportunities, evaluate options, and develop strategic recommendations tailored to your specific situation.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-spire-purple to-spire-green flex items-center justify-center text-white text-2xl font-bold mr-4">
                    3
                  </div>
                  <h3 className="text-3xl font-bold">Negotiate & Execute</h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Armed with market intelligence and strategic insights, we negotiate on your behalf to secure the best possible terms. We handle letters of intent, offers to lease, purchase agreements, and all documentation, ensuring your interests are protected at every step while maximizing value and minimizing risk.
                </p>
              </div>
              <div className="bg-gradient-to-br from-spire-purple/10 via-spire-green/10 to-transparent rounded-3xl p-8 h-64 flex items-center justify-center">
                <Briefcase className="text-spire-purple" size={80} />
              </div>
            </div>

            {/* Step 4 */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="order-2 md:order-1 bg-gradient-to-br from-spire-green/10 to-transparent rounded-3xl p-8 h-64 flex items-center justify-center">
                <Award className="text-spire-green" size={80} />
              </div>
              <div className="order-1 md:order-2">
                <div className="flex items-center mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-spire-green to-spire-green-dark flex items-center justify-center text-white text-2xl font-bold mr-4">
                    4
                  </div>
                  <h3 className="text-3xl font-bold">Support & Follow Through</h3>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Our commitment doesn't end at closing. We stay by your side to ensure smooth transitions, address any post-closing matters, and provide ongoing support. We're invested in your long-term success and remain available as your trusted real estate advisor for future needs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="section-padding bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12">What You Can Expect</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="card-feature">
              <h3 className="text-xl font-bold mb-4">Documentation</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Letters of intent</li>
                <li>• Offers to lease</li>
                <li>• Lease agreements - new & renewal</li>
                <li>• Purchase and sale agreements</li>
              </ul>
            </div>
            <div className="card-feature">
              <h3 className="text-xl font-bold mb-4">Analysis & Strategy</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Market and demographic analysis</li>
                <li>• Site selection and acquisition</li>
                <li>• Real estate strategy development</li>
                <li>• Feasibility studies</li>
              </ul>
            </div>
            <div className="card-feature">
              <h3 className="text-xl font-bold mb-4">Ongoing Support</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Construction support</li>
                <li>• Build-out coordination</li>
                <li>• Regulatory compliance guidance</li>
                <li>• Post-closing follow-up</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-spire-purple">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Experience Our Method</h2>
          <p className="text-xl text-white/90 mb-10">
            Let us guide you through a seamless real estate experience.
          </p>
          <Link to="/contact" className="px-10 py-4 bg-white text-spire-purple font-semibold rounded-full hover:shadow-2xl transition-all inline-flex items-center justify-center text-lg">
            Get Started <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

// Contact Page
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      setSubmitStatus({ type: 'success', message: 'Thank you! We\'ll contact you within 24 hours.' });
      setFormData({ name: '', email: '', phone: '', company: '', service: '', message: '' });
    } catch (error) {
      setSubmitStatus({ type: 'error', message: 'Something went wrong. Please try again or call us directly.' });
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-white">
      {/* Hero */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1746021375258-79fa1464ca1f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
            alt="Modern Medical Office Interior"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-spire-purple/95 to-spire-purple/80"></div>
        </div>
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Let's Connect</h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Schedule a consultation with our expert team
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="text-3xl font-bold mb-8">Get In Touch</h2>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Our team is ready to answer your questions and help you with your real estate needs. Contact us today for a consultation.
              </p>

              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-14 h-14 rounded-xl bg-spire-purple/10 flex items-center justify-center mr-4 flex-shrink-0">
                    <Phone className="text-spire-purple" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Phone</h3>
                    <p className="text-gray-600 text-lg">Contact us by email below</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-14 h-14 rounded-xl bg-spire-purple/10 flex items-center justify-center mr-4 flex-shrink-0">
                    <Mail className="text-spire-purple" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Email</h3>
                    <a href="mailto:info@spiregrp.com" className="text-gray-600 hover:text-spire-purple transition text-lg">
                      info@spiregrp.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-14 h-14 rounded-xl bg-spire-purple/10 flex items-center justify-center mr-4 flex-shrink-0">
                    <MapPin className="text-spire-purple" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">Serving North America</h3>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 bg-gradient-to-br from-spire-purple/10 to-spire-green/10 rounded-2xl">
                <h3 className="font-bold text-lg mb-3">Why Choose Spire Group?</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                    Extensive North America expertise
                  </li>
                  <li className="flex items-start">
                    <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                    4,000+ commercial agreements completed
                  </li>
                  <li className="flex items-start">
                    <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                    $600M+ in avoidable costs saved for clients
                  </li>
                  <li className="flex items-start">
                    <Check className="text-spire-green mr-2 mt-1 flex-shrink-0" size={18} />
                    Client-first approach always
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="card-elevated">
                <h3 className="text-2xl font-bold mb-8">Send Us a Message</h3>
                
                {submitStatus && (
                  <div className={`p-4 rounded-xl mb-6 ${submitStatus.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    {submitStatus.message}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="input-minimal"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-minimal"
                      placeholder="your.email@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-minimal"
                      placeholder="(403) 555-1234"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Company</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="input-minimal"
                      placeholder="Your company name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Service Interest *</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="input-minimal"
                    >
                      <option value="">Select a service</option>
                      <option value="healthcare">Healthcare Real Estate</option>
                      <option value="site-selection">Site Selection & Demographics</option>
                      <option value="lease">Lease Negotiation & Renewal</option>
                      <option value="consultation">General Consultation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Message *</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="4"
                      className="input-minimal"
                      placeholder="Tell us about your real estate needs..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-spire-primary w-full text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Full Site Wrapper
const FullSite = () => {
  return (
    <SiteContext.Provider value={{ healthcareOnly: false, basePath: '/full-site' }}>
      <Navigation basePath="/full-site" />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/our-story" element={<OurStory />} />
        <Route path="/services" element={<Services />} />
        <Route path="/our-method" element={<OurMethod />} />
        <Route path="/team" element={<Team />} />
        <Route path="/contact" element={<Contact />} />
      </Routes>
      <Footer />
      <Chatbot />
    </SiteContext.Provider>
  );
};

// Healthcare Only Wrapper (Main Site)
const HealthcareOnly = () => {
  return (
    <SiteContext.Provider value={{ healthcareOnly: true, basePath: '' }}>
      <Navigation basePath="" />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/our-story" element={<OurStory />} />
        <Route path="/services" element={<Services />} />
        <Route path="/our-method" element={<OurMethod />} />
        <Route path="/team" element={<Team />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/design-preview" element={<DesignPreview />} />
        <Route path="/preview/*" element={<PreviewSite />} />
      </Routes>
      <Footer />
      <Chatbot healthcareOnly={true} />
    </SiteContext.Provider>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/*" element={<HealthcareOnly />} />
          <Route path="/testing-portal" element={<LandingPage />} />
          <Route path="/full-site/*" element={<FullSite />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;